<script setup lang="ts">
import { computed } from 'vue';
import { useInstitutionStore } from '~/entities/institution/@x/product'
import type { ProductTag } from '~/utils/types/ProductTag';

// Интерфейс для информации о скидке
export interface DiscountInfo {
  type?: 'percentage' | 'fixed';
  value: number;
  show?: boolean;
  basePrice?: number;
  finalPrice?: number;
}

const props = defineProps<{
  /** Массив ID тегов или старый единичный тег */
  tags?: number[] | number | null;
  /** Старое поле teg для обратной совместимости */
  teg?: number | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string | null;
  /** Максимальное количество отображаемых тегов */
  maxVisible?: number;
  /** Информация о скидке (для комбо-товаров) */
  discount?: DiscountInfo | null;
}>();

const institutionStore = useInstitutionStore();

// Преобразуем теги в массив ID
const tagIds = computed<number[]>(() => {
  // Приоритет: tags > teg
  if (Array.isArray(props.tags)) {
    return props.tags;
  } else if (typeof props.tags === 'number') {
    return [props.tags];
  } else if (typeof props.teg === 'number') {
    return [props.teg];
  }
  return [];
});

// Получаем теги из store
const productTags = computed<ProductTag[]>(() => {
  if (tagIds.value.length === 0) return [];
  return institutionStore.getTagsByIds(tagIds.value);
});

// Видимые теги с учетом лимита
const visibleTags = computed<ProductTag[]>(() => {
  if (!props.maxVisible || props.maxVisible >= productTags.value.length) {
    return productTags.value;
  }
  return productTags.value.slice(0, props.maxVisible);
});

// Количество скрытых тегов
const hiddenCount = computed<number>(() => {
  if (!props.maxVisible) return 0;
  return Math.max(0, productTags.value.length - props.maxVisible);
});

const badgeSize = computed(() => props.size || 'sm');

// Функция для получения цвета тега с fallback
const getTagColor = (tag: ProductTag): string => {
  return tag.color || '#757575';
};

// Функция для получения контрастного цвета текста
const getTextColor = (backgroundColor: string): string => {
  // Простая функция для определения яркости цвета
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

// Преобразуем название иконки из БД в формат Iconify
const getIconName = (iconName: string | null): string | undefined => {
  if (!iconName) return undefined;

  // Если иконка уже в формате i-collection-name, возвращаем как есть
  if (iconName.startsWith('i-')) return iconName;

  // Преобразуем название иконки в формат Iconify
  // Например: 'fire' -> 'i-heroicons-fire'
  return `i-heroicons-${iconName}`;
};

// Создаем custom UI конфигурацию для кастомного цвета
const getCustomUI = (tag: ProductTag) => {
  const bgColor = getTagColor(tag);
  const textColor = getTextColor(bgColor);

  return {
    base: 'inline-flex items-center font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
    rounded: 'rounded-md',
    variant: {
      solid: `shadow-sm`,
    },
  };
};

// Показывать ли badge скидки
const showDiscountBadge = computed(() => {
  return props.discount && props.discount.show !== false && props.discount.value > 0;
});

// Текст скидки
const discountText = computed(() => {
  if (!props.discount) return '';

  if (props.discount.type === 'fixed') {
    return `-${props.discount.value}₽`;
  }

  // По умолчанию процентная скидка
  return `-${props.discount.value}%`;
});
</script>

<template>
  <div v-if="showDiscountBadge || productTags.length > 0" :class="className" class="flex flex-wrap gap-1.5">
    <!-- Badge скидки (приоритет - показывается первым) -->
    <UBadge
      v-if="showDiscountBadge"
      :label="discountText"
      icon="i-heroicons-tag"
      :size="badgeSize"
      variant="solid"
      class="badge-discount"
      :ui="{
        base: 'inline-flex items-center font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg',
        rounded: 'rounded-md',
        variant: {
          solid: 'shadow-md',
        },
      }"
      :style="{
        backgroundColor: '#ef4444',
        color: '#ffffff',
      }"
    />

    <!-- UBadge для каждого тега с цветом и иконкой из БД -->
    <UBadge
      v-for="tag in visibleTags"
      :key="tag.id"
      :label="tag.label"
      :icon="getIconName(tag.icon)"
      :size="badgeSize"
      variant="solid"
      :ui="getCustomUI(tag)"
      :style="{
        backgroundColor: getTagColor(tag),
        color: getTextColor(getTagColor(tag)),
      }"
      class="badge-custom"
    />

    <!-- Индикатор дополнительных тегов -->
    <UBadge
      v-if="hiddenCount > 0"
      :label="`+${hiddenCount}`"
      :size="badgeSize"
      color="gray"
      variant="soft"
      class="badge-more"
    />
  </div>
</template>

<style scoped>
/* Кастомные стили для badge с плавными переходами */
.badge-custom {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.badge-custom:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.15), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
}

.badge-discount {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: pulse-subtle 2s ease-in-out infinite;
}

.badge-discount:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 12px -1px rgba(239, 68, 68, 0.3), 0 4px 6px -1px rgba(239, 68, 68, 0.2);
}

@keyframes pulse-subtle {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.badge-more {
  cursor: default;
}
</style>
